import { Component, OnInit, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ReportConstants } from '../../shared/constants/report.constants';

import { ReportService, ExportService, ExportRequestInputExportType, ExportRequestInput } from '../../../service/proxy';
import { ToastService } from '../../shared/toast/toast.service';
import { AuthService } from '../../shared/services/auth.service';

@Component({
    selector: 'app-report-viewer',
    templateUrl: './report-viewer.component.html',
    styleUrls: ['./report-viewer.component.scss'],
    providers: [ReportService, ExportService],
    imports: [CommonModule],
})

export class ReportViewerComponent implements OnInit {
    public reportType: string = '';
    public reportParams: any;
    public htmlContent: string = '';
    public loading: boolean = false;
    public isChecklist = false;
    public isAnnexureExport = false;
    public isAgeingExport = false;
    public safeHtml!: SafeHtml;

    constructor(
        private route: ActivatedRoute,
        private reportService: ReportService,
        private exportService: ExportService,
        private authService: AuthService,
        private toastService: ToastService,
        private sanitizer: DomSanitizer,
        private router: Router,
    ) {}

    ngOnInit() {
        this.reportType = this.route.snapshot.paramMap.get('reportType') ?? '';
        this.isChecklist = this.reportType === ReportConstants.TurnoverChecklistReport;
        this.isAnnexureExport = this.reportType === ReportConstants.AnnexureExportReport;
        this.isAgeingExport = this.reportType === ReportConstants.AgeingExportReport;

        this.reportParams = (window.history.state.reportParams || []).sort((a: any, b: any) => a.index - b.index);

        this.loadReport();
    }

    loadReport() {
        const reportServiceMappings = this.getReportMappings();
        const serviceMethod = reportServiceMappings[this.reportType];

        if (!serviceMethod) {
            this.toastService.show('No report method found for this report type.', 'error');

            return;
        }

        this.loading = true;
        this.htmlContent = '';

        const params = this.buildServiceParams('');

        serviceMethod
            .apply(this.reportService, params)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (response: { htmlContent: string }) => {
                    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(response?.htmlContent ?? '');
                },
                error: () => {
                    this.toastService.show('Failed to load report. Please try again.', 'error');
                },
            });
    }

    onExcelAction(): void {
        if (this.isAnnexureExport) {
            this.emailAnnexureExport('excel');
        }
        else if (this.isAgeingExport) {
            this.emailAgeingExport('excel');
        }
        else {
            this.exportReport('excel');
        }
    }

    onPdfAction(): void {
        if (this.isAnnexureExport) {
            this.emailAnnexureExport('pdf');
        }
        else if (this.isAgeingExport) {
            this.emailAgeingExport('pdf');
        }
        else {
            this.exportReport('pdf');
        }
    }

    private getParamValue(labelOrKey: string): any {
        if (!Array.isArray(this.reportParams)) {
            return null;
        }

        const match = this.reportParams.find(
            (p: any) => p?.label === labelOrKey || p?.key === labelOrKey
        );

        return match ? match.key : null;
    }

    private async emailAnnexureExport(exportType: 'pdf' | 'excel'): Promise<void> {
        if (!Array.isArray(this.reportParams) || this.reportParams.length < 3) {
            this.toastService.show(
                'Report parameters are missing. Please go back to the Annexure Export screen and run the report again.',
                'error'
            );
            return;
        }

        const rawArgs = this.buildServiceParams('' as any);

        const [startRaw, endRaw, insurerRaw] = rawArgs;

        const startPeriodId = Number(startRaw);
        const endPeriodId = Number(endRaw);
        const insurerId = Number(insurerRaw);

        const invalid =
            !Number.isFinite(startPeriodId) ||
            !Number.isFinite(endPeriodId) ||
            !Number.isFinite(insurerId);

        if (invalid) {
            this.toastService.show(
                'Missing parameters for annexure export email (periods or insurer).',
                'error'
            );
            return;
        }

        // Get user email for notification
        const emailAddress = await this.authService.getUserEmail();
        if (!emailAddress) {
            this.toastService.show(
                'Unable to determine your email address. Please try again.',
                'error'
            );
            return;
        }

        this.toastService.show(
            `Queuing annexure export (${exportType.toUpperCase()}) ...`,
            'info'
        );

        const request = new ExportRequestInput({
            exportType: ExportRequestInputExportType.Annexure,
            emailAddress,
            startPeriodId,
            endPeriodId,
            insurerId,
            // Client area not needed for annexure export
            clientAreaId: 0,
            startDate: undefined as any,
            endDate: undefined as any,
        });

        this.exportService
            .createExportRequest(request)
            .subscribe({
                next: () => {
                    this.toastService.show(
                        'Annexure export queued. You will receive an email when it is ready.',
                        'success'
                    );
                },
                error: (err: Error) => {
                    console.error('createExportRequest error', err);
                    this.toastService.show(
                        'Failed to queue annexure export. Please try again.',
                        'error'
                    );
                },
            });
    }

    private async emailAgeingExport(exportType: 'pdf' | 'excel'): Promise<void> {
        if (!Array.isArray(this.reportParams) || this.reportParams.length < 3) {
            this.toastService.show(
                'Report parameters are missing. Please go back to the Ageing Export screen and run the report again.',
                'error'
            );
            return;
        }

        const rawArgs = this.buildServiceParams('' as any);

        // Ageing export expects: startPeriodId, endPeriodId, insurerId (same as annexure)
        const [startRaw, endRaw, insurerRaw] = rawArgs;

        const startPeriodId = Number(startRaw);
        const endPeriodId = Number(endRaw);
        const insurerId = Number(insurerRaw);

        const invalid =
            !Number.isFinite(startPeriodId) ||
            !Number.isFinite(endPeriodId) ||
            !Number.isFinite(insurerId);

        if (invalid) {
            this.toastService.show(
                'Missing parameters for ageing export (periods or insurer).',
                'error'
            );
            return;
        }

        // Get user email for notification
        const emailAddress = await this.authService.getUserEmail();
        if (!emailAddress) {
            this.toastService.show(
                'Unable to determine your email address. Please try again.',
                'error'
            );
            return;
        }

        this.toastService.show(
            `Queuing ageing export (${exportType.toUpperCase()}) ...`,
            'info'
        );

        const request = new ExportRequestInput({
            exportType: ExportRequestInputExportType.Ageing,
            emailAddress,
            startPeriodId,
            endPeriodId,
            insurerId,
            startDate: undefined as any,
            endDate: undefined as any,
        });

        this.exportService
            .createExportRequest(request)
            .subscribe({
                next: () => {
                    this.toastService.show(
                        'Ageing export queued. You will receive an email when it is ready.',
                        'success'
                    );
                },
                error: (err: Error) => {
                    console.error('createExportRequest error', err);
                    this.toastService.show(
                        'Failed to queue ageing export. Please try again.',
                        'error'
                    );
                },
            });
    }

    private buildServiceParams(exportType: 'pdf' | 'excel' | '') {

        const reportsWithoutDates = [ReportConstants.SharedDebtorByPolicyReport];
        const reportsWithoutClientId = [
            ReportConstants.PremiumsReport,
            ReportConstants.ClaimsReport,
            ReportConstants.LimitsReport,
            ReportConstants.PerformanceBonusReport
        ];

        const downloadReportsThatNeedClientId = [
            ReportConstants.DataStatusReport
        ];

        const args = this.reportParams.map((param: any) => {

                // Date handling: convert to Date only for reports that require dates
                if ((param.label === 'Start Date' || param.label === 'End Date')
                    && !reportsWithoutDates.includes(this.reportType)) {
                    return new Date(param.key);
                }

                 // Client ID handling: if caller requests exclusion OR report is known to not use client id -> skip
                if ((param.label === 'Client ID' || param.label === 'Client')
                    && 
                    (
                        (((exportType == 'excel' || exportType == 'pdf') && !downloadReportsThatNeedClientId.includes(this.reportType))
                        || reportsWithoutClientId.includes(this.reportType)))
                    ) 
                    {
                        return null;
                    }

                return param.key;
            })
            .filter((v: any) => v != null); 

        if (exportType && (exportType === 'pdf' || exportType === 'excel')) {
            args.push(exportType);
        }

        return args;

    }

    exportReport(exportType: 'pdf' | 'excel') {
        if (exportType === 'pdf') {
            this.toastService.show('Exporting to PDF...', 'Info');
        }
        else {
            this.toastService.show('Exporting to Excel...', 'Info');
        }

        const reportServiceMappings = this.getExportMappings();
        const serviceMethod = reportServiceMappings[this.reportType];

        if (!serviceMethod) {
            console.error('No service method found for report type:', this.reportType);
            this.toastService.show('Export method not available for this report.', 'error');
            return;
        }

        const args = this.buildServiceParams(exportType);

        if (!args) {
            console.error('Failed to build export arguments');
            this.toastService.show('Failed to prepare export parameters.', 'error');
            return;
        }

        try {
            const result = serviceMethod.apply(this.reportService, args);

            result.subscribe({
                next: (response: any) => {
                    let blob: Blob;
                    let fileName: string;

                    if (response && response.data && response.data instanceof Blob) {
                        blob = response.data;
                        fileName = response.fileName || this.generateFileName(exportType);
                    }
                    else if (response instanceof Blob) {
                        blob = response;
                        fileName = this.generateFileName(exportType);
                    }
                    else {
                        console.error('Response is not a blob:', response);
                        return;
                    }

                    if (!blob || blob.size === 0) {
                        console.error('Blob is empty or null');
                        return;
                    }

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;

                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
                error: (error: any) => {
                    this.toastService.show('Failed to export report. Please try again.', 'error');
                },
            });
        }
        catch (error) {
            this.toastService.show('An unexpected error occurred while exporting the report.', 'error');
        }
    }

    backtoReportSelection(): void {
        try {

            // The Declaration General Reports don't get their parameters repopulated - just redirect to selection
            if (this.reportType === ReportConstants.AgeingExportReport)
            {
                this.router.navigate(['/ageing-export-parameters']);
                return;
            }

            if (this.reportType === ReportConstants.AnnexureExportReport)
            {
                this.router.navigate(['/annexure-export-parameters']);
                return;
            }

            if (this.reportType === ReportConstants.DataStatusReport)
            {
                this.router.navigate(['/data-status-report-parameters']);
                return;
            }

            if (this.reportType === ReportConstants.SharedDebtorByPolicyReport)
            {
                this.router.navigate(['/shared-debtor-by-policy-parameters']);
                return;
            }

            const hasPeriod = Array.isArray(this.reportParams) &&
                this.reportParams.some((p: any) => p && (p.label === 'Period' || p.key === 'PeriodId'));

            const route = hasPeriod ? '/declaration-report-selection' : '/renewal-report-selection';
            this.router.navigate([route], { state: { reportParams: this.reportParams } });
        }
        catch (e) {
            // Fallback to history back if navigate fails
            window.history.back();
        }
    }

    private generateFileName(exportType: 'pdf' | 'excel'): string {
        const fileExtension = exportType === 'pdf' ? 'pdf' : 'xlsx';

        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const friendlyName = ReportConstants.friendlyNames[this.reportType] || this.reportType;

        const cleanName = friendlyName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');

        return `${cleanName}_${timestamp}.${fileExtension}`;
    }

    getReportMappings(): Record<string, (...args: any[]) => any> {
        return {
            [ReportConstants.AgeingExportReport]: this.reportService.getAgeingExportData,
            [ReportConstants.AnnexureExportReport]: this.reportService.getAnnexureExportReportData,
            [ReportConstants.ClaimsReport]: this.reportService.getClaimsReportData,
            [ReportConstants.CheckListAgeingReport]: this.reportService.getTurnoverChecklistReportData,
            [ReportConstants.CompleteRecordsReport]: this.reportService.getCompleteRecordsData,
            [ReportConstants.DataStatusReport]: this.reportService.getDataStatusReportData,
            [ReportConstants.DeclarationDetailReport]: this.reportService.getDeclarationDetailReportData,
            [ReportConstants.DeclarationSummaryReport]: this.reportService.getDeclarationSummaryReportData,
            [ReportConstants.DetailedTurnOverReport]: this.reportService.getDetailedTurnoverReportData,
            [ReportConstants.DivisionExposureReport]: this.reportService.getDivisionExposureReport,
            //[ReportConstants.DivisionTotalsReport]: this.reportService.getDivisionTotalsData,
            [ReportConstants.FranchiseLossLimitReport]: this.reportService.getFranchiseLossReportData,
            [ReportConstants.ImportDetailsReport]: this.reportService.getImportDetailsReportData,
            [ReportConstants.InsuredDebtorReport]: this.reportService.getInsuredDebtorsReportData,
            [ReportConstants.InsuredDebtorsGrantedVsExposures]: this.reportService.getInsuredDebtorsGrantedLimitsVsExposuresReportData,
            [ReportConstants.LimitValueChangesReport]: this.reportService.getLimitValueChangesReportData,
            [ReportConstants.LimitsReport]: this.reportService.getLimitsReportData,
            [ReportConstants.LinkedDebtorReport]: this.reportService.getLinkedDebtorData,
            [ReportConstants.NegativeBalanceReport]: this.reportService.getNegativeBalanceReportData,
            [ReportConstants.OverdueAccountsDetailedReport]: this.reportService.getOverdueAccountsDetailedReportData,
            [ReportConstants.OverdueAccountsReport]: this.reportService.getOverdueAccountsReportData,
            [ReportConstants.OvertradesReport]: this.reportService.getOvertradesReportContent,
            [ReportConstants.PerformanceBonusReport]: this.reportService.getPerformanceBonusReportData,
            [ReportConstants.PolicyScheduleReport]: this.reportService.getPolicyScheduleReportData,
            [ReportConstants.PremiumsReport]: this.reportService.getPremiumSummaryReport,
            //[ReportConstants.ReportPack]: this.reportService.getReportPackData,
            [ReportConstants.SharedDebtorByPolicyReport]: this.reportService.getSharedDebtorByPolicyReportData,
            [ReportConstants.SpecialConditionsReport]: this.reportService.getSpecialConditionsReportContent,
            [ReportConstants.TwelveMonthUtilizationReport]: this.reportService.getTwelveMonthUtilizationReportData,
            [ReportConstants.TurnoverReport]: this.reportService.getTurnoverReportData,
            [ReportConstants.TurnoverChecklistReport]: this.reportService.getTurnoverChecklistReportData,
            [ReportConstants.UninsurableDebtorReport]: this.reportService.getUninsurableDebtorReportData,
            [ReportConstants.UninsuredDebtorReport]: this.reportService.getUninsuredDebtorReportData,
            [ReportConstants.UnutilizedInsurerLimitsReport]: this.reportService.getUnutilizedInsurerLimitsReportData,
            [ReportConstants.UserReport]: this.reportService.getUserReportData,
            [ReportConstants.Utilisation12MonthReport]: this.reportService.getTwelveMonthUtilizationReportData,
        };
    }

    getExportMappings(): Record<string, (...args: any[]) => any> {
        return {
            // Note: AgeingExportReport and AnnexureExportReport use async queue/email flow via /export endpoints
            [ReportConstants.ClaimsReport]: this.reportService.downloadClaimsReport,
            [ReportConstants.CheckListAgeingReport]: this.reportService.downloadTurnoverChecklistReport,
            [ReportConstants.CompleteRecordsReport]: this.reportService.downloadCompleteRecords,
            [ReportConstants.DataStatusReport]: this.reportService.downloadDataStatusReport,
            [ReportConstants.DeclarationDetailReport]: this.reportService.downloadDeclarationDetailReport,
            [ReportConstants.DeclarationSummaryReport]: this.reportService.downloadDeclarationSummaryReport,
            [ReportConstants.DetailedTurnOverReport]: this.reportService.downloadDetailedTurnoverReport,
            [ReportConstants.DivisionExposureReport]: this.reportService.downloadDivisionExposureReport,
            //[ReportConstants.DivisionTotalsReport]: this.reportService.downloadDivisionTotalsReport,
            [ReportConstants.FranchiseLossLimitReport]: this.reportService.downloadFranchiseLossReport,
            [ReportConstants.ImportDetailsReport]: this.reportService.downloadImportDetailsReport,
            [ReportConstants.InsuredDebtorReport]: this.reportService.downloadInsuredDebtorsReport,
            [ReportConstants.InsuredDebtorsGrantedVsExposures]: this.reportService.downloadInsuredDebtorsGrantedLimitsVsExposuresReport,
            [ReportConstants.LimitValueChangesReport]: this.reportService.downloadLimitValueChangesReport,
            [ReportConstants.LimitsReport]: this.reportService.downloadLimitsReport,
            [ReportConstants.LinkedDebtorReport]: this.reportService.downloadLinkedDebtor,
            [ReportConstants.NegativeBalanceReport]: this.reportService.downloadNegativeBalanceReport,
            [ReportConstants.OverdueAccountsDetailedReport]: this.reportService.downloadOverdueAccountsDetailedReport,
            [ReportConstants.OverdueAccountsReport]: this.reportService.downloadOverdueAccountsReport,
            [ReportConstants.OvertradesReport]: this.reportService.downloadOvertradesReport,
            [ReportConstants.PerformanceBonusReport]: this.reportService.downloadPerformanceData,
            [ReportConstants.PolicyScheduleReport]: this.reportService.downloadPolicyScheduleReport,
            [ReportConstants.PremiumsReport]: this.reportService.downloadPremiumSummaryReport,
            //[ReportConstants.ReportPack]: this.reportService.downloadReportPack,
            [ReportConstants.SharedDebtorByPolicyReport]: this.reportService.downloadSharedDebtorByPolicyReport,
            [ReportConstants.SpecialConditionsReport]: this.reportService.downloadSpecialConditionsReport,
            [ReportConstants.TwelveMonthUtilizationReport]: this.reportService.downloadTwelveMonthUtilizationReport,
            [ReportConstants.TurnoverReport]: this.reportService.downloadTurnoverReport,
            [ReportConstants.TurnoverChecklistReport]: this.reportService.downloadTurnoverChecklistReport,
            [ReportConstants.UninsurableDebtorReport]: this.reportService.downloadUninsurableDebtor,
            [ReportConstants.UninsuredDebtorReport]: this.reportService.downloadUninsuredDebtorReport,
            [ReportConstants.UnutilizedInsurerLimitsReport]: this.reportService.downloadUnutilizedInsurerLimitsReport,
            [ReportConstants.UserReport]: this.reportService.downloadUserReport,
            [ReportConstants.Utilisation12MonthReport]: this.reportService.downloadTwelveMonthUtilizationReport,
        };
    }
}
